import React, { createContext, useContext, useEffect, useState, useCallback } from "react";
import { auth, sponsorApi, journals as journalsApi, savedWisdom as savedWisdomApi } from "../lib/api";
import { getGuest, clearGuest } from "../lib/guest";

const AuthContext = createContext(null);

// 30-day sponsor attribution window (must match backend
// ATTRIBUTION_WINDOW_DAYS). If a captured referral is older than this we
// discard it silently rather than crediting the sponsor for a stale visit.
const ATTRIBUTION_WINDOW_MS = 30 * 24 * 60 * 60 * 1000;

async function _tryAttribute() {
  try {
    const raw = localStorage.getItem("lg_sponsor_ref");
    if (!raw) return;
    const parsed = JSON.parse(raw);
    if (!parsed?.referral_code || !parsed?.at) return;
    if (Date.now() - parsed.at > ATTRIBUTION_WINDOW_MS) {
      // Expired — drop the ref so we do not keep retrying.
      localStorage.removeItem("lg_sponsor_ref");
      return;
    }
    await sponsorApi.attribute(parsed.referral_code);
    // Successful attribution or first-wins refusal — either way, clear so
    // we do not call again on every session.
    localStorage.removeItem("lg_sponsor_ref");
  } catch {
    // Non-fatal; retry on the next auth event.
  }
}

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(async () => {
    const token = localStorage.getItem("lg_token");
    if (!token) {
      setUser(null);
      setLoading(false);
      return;
    }
    try {
      const u = await auth.me();
      setUser(u);
    } catch {
      localStorage.removeItem("lg_token");
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const login = async (email, password) => {
    const res = await auth.login({ email, password });
    localStorage.setItem("lg_token", res.token);
    setUser(res.user);
    // Clear any lingering anonymous guest-trial state so its expiry can
    // never override the authenticated account's real entitlement (fixes
    // the case where a prior expired `lg_guest` blocked a Pro user
    // arriving via the I-Need-a-Proverb Say-It-Better handoff).
    clearGuest();
    _tryAttribute();
  };

  const signup = async (email, password, name, extras = {}) => {
    // If a guest preview exists, pass its id so the backend can record the
    // guest → account conversion. Then clear the local guest state.
    const g = getGuest();
    const payload = { email, password, name, ...extras };
    if (g?.id) payload.guest_id = g.id;
    const res = await auth.signup(payload);
    localStorage.setItem("lg_token", res.token);
    setUser(res.user);
    // Merge any guest scratch (Journal + Saved Wisdom) into the real
    // account. Best-effort — failures never block signup UX.
    if (g?.id) {
      try {
        const scratchJournal = Array.isArray(g.journal_scratch) ? g.journal_scratch : [];
        for (const row of scratchJournal) {
          try {
            await journalsApi.create({
              title: row.title || "",
              content: row.content || "",
              source: "guest_scratch",
            });
          } catch {
            /* ignore individual merge failure */
          }
        }
        const scratchWisdom = Array.isArray(g.wisdom_scratch) ? g.wisdom_scratch : [];
        for (const row of scratchWisdom) {
          try {
            await savedWisdomApi.create({
              kind: "guidance",
              reference_id: row.id,
              title: row.title || "Saved wisdom",
              body: row.body || "",
              meta: { scripture_reference: row.reference || null, from: "guest_scratch" },
            });
          } catch {
            /* ignore individual merge failure */
          }
        }
      } catch {
        /* whole merge best-effort */
      }
      clearGuest();
    }
    _tryAttribute();
  };

  const logout = () => {
    localStorage.removeItem("lg_token");
    setUser(null);
  };

  const upgrade = async () => {
    const u = await auth.upgrade();
    setUser(u);
  };

  const demoLogin = async () => {
    const res = await auth.demo();
    localStorage.setItem("lg_token", res.token);
    setUser(res.user);
    return res.user;
  };

  return (
    <AuthContext.Provider value={{ user, loading, login, signup, logout, upgrade, demoLogin, refresh, setUser }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => useContext(AuthContext);
